import { AxiosResponse, InternalAxiosRequestConfig } from './types'
import { ElMessage } from 'element-plus'
import qs from 'qs'
import { SUCCESS_CODE, TRANSFORM_REQUEST_DATA } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { objToFormData } from '@/utils'

const defaultRequestInterceptors = (config: InternalAxiosRequestConfig) => {
  if (
    config.method === 'post' &&
    config.headers['Content-Type'] === 'application/x-www-form-urlencoded'
  ) {
    config.data = qs.stringify(config.data)
  } else if (
    TRANSFORM_REQUEST_DATA &&
    config.method === 'post' &&
    config.headers['Content-Type'] === 'multipart/form-data' &&
    !(config.data instanceof FormData)
  ) {
    config.data = objToFormData(config.data)
  }
  if (config.method === 'get' && config.params) {
    let url = config.url as string
    url += '?'
    const keys = Object.keys(config.params)
    const append = (k: string, val: unknown) => {
      url += `${encodeURIComponent(k)}=${encodeURIComponent(String(val))}&`
    }
    for (const key of keys) {
      const v = config.params[key]
      if (v === undefined || v === null || v === '') {
        continue
      }
      if (Array.isArray(v)) {
        for (const item of v) {
          if (item === undefined || item === null || item === '') {
            continue
          }
          append(key, item)
        }
      } else if (typeof v === 'object') {
        // 避免 [object Object] 导致服务端绑定/反序列化异常
        continue
      } else {
        append(key, v)
      }
    }
    url = url.substring(0, url.length - 1)
    config.params = {}
    config.url = url
  }
  return config
}
// new
const defaultResponseInterceptors = (response: AxiosResponse) => {
  // 检查响应头中是否有新的访问令牌
  if (response.headers && response.headers['new-access-token']) {
    const newAccessToken = response.headers['new-access-token']
    if (newAccessToken) {
      const extractedToken = newAccessToken.substring(7) // 移除 "Bearer " 前缀
      // 更新本地存储中的token
      localStorage.setItem('accessToken', extractedToken)
      // 更新状态管理中的token
      import('@/store/modules/user')
        .then(({ useUserStoreWithOut }) => {
          const userStore = useUserStoreWithOut()
          userStore.setToken(extractedToken)
        })
        .catch((error) => {
          console.error('Failed to update token in store:', error)
          // 如果状态管理更新失败，至少保证本地存储被更新
          localStorage.setItem('accessToken', extractedToken)
        })
    }
  }

  if (response?.config?.responseType === 'blob') {
    const contentType = String(response.headers?.['content-type'] || '').toLowerCase()
    // 明确的文件流不要先 text()，否则部分浏览器/场景下保存会得到空文件
    const looksLikeFile =
      contentType.includes('application/octet-stream') ||
      contentType.includes('application/vnd') ||
      contentType.includes('application/zip') ||
      contentType.includes('application/pdf') ||
      contentType.includes('image/') ||
      contentType.includes('spreadsheet') ||
      contentType.includes('ms-excel')
    if (looksLikeFile) {
      return response
    }
    // 处理 Blob 响应中的错误信息（后端异常时常以 JSON + blob 返回）
    return response.data
      .text()
      .then((text: string) => {
        try {
          const jsonData = JSON.parse(text)
          // 检查是否是错误响应
          if (jsonData.code !== SUCCESS_CODE) {
            const errorMessage = jsonData?.message || `请求失败，状态码: ${jsonData.code}`
            const silentError = (response.config as InternalAxiosRequestConfig & { silentError?: boolean })
              ?.silentError
            if (!silentError) {
              ElMessage.error(errorMessage)
            }

            // 如果是登录失效
            const isBusinessLoginExpired =
              jsonData &&
              (jsonData.code === 401 ||
                jsonData.message === 'Unauthorized' ||
                jsonData.message === '登录已失效' ||
                jsonData.message === '需要登录')

            if (isBusinessLoginExpired) {
              // 处理登录失效逻辑
              setTimeout(() => {
                handleAuthFailure()
              }, 1500)

              return {
                ...jsonData,
                _isLoginExpired: true
              }
            }

            // 返回错误信息供上层处理
            return {
              ...jsonData,
              _isError: true
            }
          }
          if (jsonData.code === 0) {
            ElMessage.success(jsonData.message)
          }
          // 成功响应直接返回
          return response
        } catch (e) {
          // 如果不是有效的JSON，认为是正常的文件流
          return response
        }
      })
      .catch((error) => {
        // 处理解析错误
        return Promise.reject(error)
      })
  } else {
    const responseData = response.data
    // 检查HTTP状态码的登录失效情况
    const status = response.status
    const isHttpLoginExpired = status === 401

    // 业务码：勿把通用 403 当作登录失效（后端常用 403 表示无权限，与 token 无关）
    const isBusinessLoginExpired =
      responseData &&
      (responseData.code === 401 ||
        responseData.message === 'Unauthorized' ||
        responseData.message === '登录已失效' ||
        responseData.message === '需要登录')

    // 如果检测到任何形式的登录失效
    if (isHttpLoginExpired || isBusinessLoginExpired) {
      // 延迟一下再跳转，让用户看到提示信息
      setTimeout(() => {
        handleAuthFailure()
      }, 1500)

      // 返回特殊标记而不是抛出错误
      return {
        ...responseData,
        _isLoginExpired: true
      }
    }

    // 如果状态码不为SUCCESS_CODE且不是登录失效，显示错误信息
    if (responseData && typeof responseData === 'object' && responseData.code !== SUCCESS_CODE && !responseData._isLoginExpired) {
      // 检查message是否包含Success，如果是则不显示错误信息
      const isSuccessfulMessage =
        responseData?.message &&
        typeof responseData.message === 'string' &&
        responseData.message.toLowerCase().includes('success')
      if (!isSuccessfulMessage && responseData?.status != 0) {
        // 代理断连/后端重启时常返回无业务 code 的空体或 HTML
        const errorMessage =
          responseData?.message ||
          (responseData?.code != null
            ? `请求失败，状态码: ${responseData.code}`
            : '服务响应异常，请稍后重试')
        ElMessage.error(errorMessage)
      }
    }

    // 直接返回响应数据
    return responseData
  }
}

// 认证失败后统一返回系统登录页
const handleAuthFailure = () => {
  const loginUrl = `/login?redirect=${encodeURIComponent(window.location.pathname)}`
  clearUserStorage()
  window.location.href = loginUrl
}

// 清除用户相关存储的函数
const clearUserStorage = () => {
  import('@/store/modules/user')
    .then(({ useUserStoreWithOut }) => {
      const userStore = useUserStoreWithOut()
      userStore.logout()
    })
    .catch(() => {
      // 如果导入失败，执行基本的清理
      localStorage.removeItem('userInfo')
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      sessionStorage.clear()
    })
}

// 错误响应拦截器 - 处理HTTP状态码错误
const defaultResponseErrorInterceptor = (error: any) => {
  if (error.response) {
    const { status, data, config } = error.response
    console.log('status,当前请求状态', status, error.response)

    // 特殊处理401和403错误，避免重复显示默认错误信息
    if (status === 401 || status === 403) {
      // 不显示默认错误，让登录失效逻辑处理
      return Promise.reject(error)
    }

    // 对于其他错误，可以自定义处理
    const errorMessage = data?.message || `请求失败，状态码: ${status}`

    // 避免重复显示错误消息
    if (!config._errorShown) {
      config._errorShown = true
      // 根据需要决定是否显示错误消息
      // ElMessage.error(errorMessage);
    }
  } else if (error.request) {
    // 网络错误
    if (error.code !== 'ERR_CANCELED') {
      // 忽略取消请求的错误
      ElMessage.error('网络错误，请检查网络连接')
    }
  } else {
    // 其他错误
    ElMessage.error(error.message || '请求配置错误')
  }

  return Promise.reject(error)
}
export { defaultResponseInterceptors, defaultRequestInterceptors, defaultResponseErrorInterceptor }
